<script lang="ts">
import { defineComponent, computed } from 'vue'
import { compile, match } from 'path-to-regexp'
import { isPositiveInteger, isNonNegativeInteger } from '@/utilities/math'

const DEFAULT_PAGINATION_QUERY = 'page'
const DEFAULT_OFFSET = 2

export default defineComponent({
  name: 'PaginationDefault',

  props: {
    /**
     * クエリパラメータでアンカーを作る場合のクエリ key 名
     * 指定なかった場合は無視される
     */
    queryParam: {
      type: String,
      default: '',
    },

    /**
     * pathPattern に含まれる、ページ番号を表すパラメータ名
     * @example
     *  "/foo/bar/:documentPage" -> path-param="documentPage"
     */
    pathParam: {
      type: String,
      default: '',
    },

    /**
     * ページ番号をパスパラメータとして埋め込む場合の、現在の URL に一致するパスパターン
     * @example
     *  "/solution/:id/documents/:documentPage"
     */
    pathPattern: {
      type: String,
      default: '',
    },

    /**
     * 現在のページ page 番号
     */
    page: {
      type: Number,
      required: true,
      validator: isPositiveInteger,
    },

    /**
     * ページの最大数
     */
    totalPage: {
      type: Number,
      required: true,
      validator: isNonNegativeInteger,
    },

    /**
     * 前後に表示するページ番号の件数
     * @example
     *  :offset="3"
     *  < 1 2 3 [4] 5 6 7 >
     */
    offset: {
      type: Number,
      default: DEFAULT_OFFSET,
      validator: isPositiveInteger,
    },
  },

  setup(props) {
    /**
     * 不正な totalPage はページなしとして扱う
     */
    const safeTotalPage = computed((): number => {
      return isNonNegativeInteger(props.totalPage)
        ? props.totalPage
        : 0
    })

    /**
     * ページが存在するか
     */
    const hasPagination = computed((): boolean => {
      return safeTotalPage.value > 0
    })

    /**
     * 不正な offset は既定値に戻す
     */
    const safeOffset = computed((): number => {
      return isPositiveInteger(props.offset)
        ? props.offset
        : DEFAULT_OFFSET
    })

    /**
     * 現在ページ
     * @description props で受け渡されたページ番号を 1 から総ページ数の範囲に収める
     *              （総ページ数が 0 のときは 0 を返す。hasPagination が false のため描画されない）
     */
    const currentPage = computed((): number => {
      const page = isPositiveInteger(props.page) ? props.page : 1
      return Math.min(page, safeTotalPage.value)
    })

    /**
     * page から offset 数分の過去ページの配列
     */
    const prev = computed((): number[] => {
      return currentPage.value > 1
        ? [...Array(safeOffset.value)]
          .map((_, i) => currentPage.value - (i + 1))
          .filter(v => v > 0)
          .reverse()
        : []
    })

    /**
     * page から offset 数分の未来ページの配列
     */
    const follow = computed((): number[] => {
      return currentPage.value < safeTotalPage.value
        ? [...Array(safeTotalPage.value - currentPage.value)]
          .map((_, i) => currentPage.value + (i + 1))
          .slice(0, safeOffset.value)
        : []
    })

    /**
     * 一つ前のページが存在するか
     */
    const hasBackward = computed((): boolean => {
      return currentPage.value > 1
    })

    /**
     * 一つ後のページが存在するか
     */
    const hasForward = computed((): boolean => {
      return currentPage.value < safeTotalPage.value
    })

    /**
     * 遷移先パス生成
     *
     * @param page 遷移先のページ番号
     */
    const navigateTo = (page: number): string => {
      const currentUrl = location.href
      const hasInvalidPathSettings = Boolean(props.pathParam) !== Boolean(props.pathPattern)
      if (hasInvalidPathSettings) {
        console.error('pathParam and pathPattern must be specified together.')
      }
      if (!isPositiveInteger(page) || hasInvalidPathSettings) return currentUrl

      const pageParam = page.toString()
      let _navigateTo = currentUrl

      // パスパラメータでのページネーション（例: /solution/:id/documents/:documentPage）
      if (props.pathParam && props.pathPattern) {
        try {
          const url = new URL(_navigateTo)
          // 現在のパスから、pathPattern に沿ってパラメータ（:id など）を取り出す
          const matched = match<Record<string, string>>(props.pathPattern, {
            decode: decodeURIComponent,
          })(url.pathname)
          // 現在の URL がパターンに一致しない、またはページ用パラメータが含まれない場合は遷移させない
          if (!matched || !(props.pathParam in matched.params)) return currentUrl

          // ページ番号だけを差し替え、他のパラメータは維持したままパスを再生成する
          // （match でデコードした値を encode で戻し、二重エンコードを防ぐ）
          url.pathname = compile<Record<string, string>>(props.pathPattern, {
            encode: encodeURIComponent,
          })({
            ...matched.params,
            [props.pathParam]: pageParam,
          })
          _navigateTo = url.href
        } catch (error) {
          console.error('Failed to build pagination path.', error)
          return currentUrl
        }
      } else {
        // クエリパラメータでのページネーション（例: ?page=2）
        const query = props.queryParam || DEFAULT_PAGINATION_QUERY
        const url = new URL(_navigateTo)
        url.searchParams.set(query, pageParam)
        _navigateTo = url.href
      }

      return _navigateTo
    }

    return {
      // computed
      hasPagination,
      currentPage,
      prev,
      follow,
      hasBackward,
      hasForward,

      // methods
      navigateTo,
    }
  },
})
</script>

<template>
  <nav
    v-if="hasPagination"
    class="o-list__pagination">
    <ul class="o-list__pagination--items">
      <!-- backward -->
      <li
        v-if="hasBackward"
        class="o-list__pagination--item is-backward">
        <div class="o-action__pagination is-backward">
          <a
            class="o-action__pagination--item"
            :href="navigateTo(currentPage - 1)">
            前へ
          </a>
        <!-- /.o-action__pagination --></div>
      </li>

      <!--
        item 間の gap を、単純に CSS の margin 等で行うと、固定値になるため、
        「5」ページ目等の中間ページを表示時で前後 item 数が長くなるケースにおいて、
        マルチカラムページ等のラッパーの幅が狭いエリアでページネーションを呼んだ際に、
        幅が足りずに「前へ」「次へ」が圧縮されてしまう等の問題があるため、
        spacer 用の DOM を gap としてレンダリングし、可変幅として運用することで、幅が足りなくなったら spacer DOM が縮む作りとする。
        ただし、spacer 用の DOM であるため、セマンティックにも気を配る必要があるため、aria-hidden="true" を付与して非表示にする。
      -->
      <li
        v-if="hasBackward"
        class="o-list__pagination--spacer"
        aria-hidden="true" />

      <!-- prev pages -->
      <template
        v-for="item in prev"
        :key="`prev-${item}`">
        <li class="o-list__pagination--item is-prev">
          <div class="o-action__pagination">
            <a
              class="o-action__pagination--item"
              :href="navigateTo(item)">
              {{ item }}
            </a>
          <!-- /.o-action__pagination --></div>
        </li>
        <li
          class="o-list__pagination--spacer"
          aria-hidden="true" />
      </template>

      <!-- current page -->
      <li class="o-list__pagination--item is-current">
        {{ currentPage }}
      </li>

      <!-- follow pages -->
      <template
        v-for="item in follow"
        :key="`follow-${item}`">
        <li
          class="o-list__pagination--spacer"
          aria-hidden="true" />
        <li class="o-list__pagination--item is-follow">
          <div class="o-action__pagination">
            <a
              class="o-action__pagination--item"
              :href="navigateTo(item)">
              {{ item }}
            </a>
          <!-- /.o-action__pagination --></div>
        </li>
      </template>

      <!-- forward page -->
      <li
        v-if="hasForward"
        class="o-list__pagination--spacer"
        aria-hidden="true" />
      <li
        v-if="hasForward"
        class="o-list__pagination--item is-forward">
        <div class="o-action__pagination is-forward">
          <a
            class="o-action__pagination--item"
            :href="navigateTo(currentPage + 1)">
            次へ
          </a>
        <!-- /.o-action__pagination --></div>
      </li>
    </ul>
  </nav>
</template>

<style lang="sass" scoped>
.o-list__pagination
  $root: &
  margin-top: 2.5em

  &:first-child
    margin-top: 0

  & &--items
    display: flex
    flex-wrap: nowrap
    justify-content: center
    align-items: center

  $button-size-px: 40
  $font-size-px: 14
  & &--item
    width: #{math.div($button-size-px, $font-size-px)}em
    line-height: #{math.div($button-size-px, $font-size-px)}em
    font-size: $font-small
    text-align: center
    border-radius: 50%
    flex-shrink: 0

    &.is-current
      color: $font-orange
      background-color: rgba($bg-x-light-orange, 1)
      cursor: default

    &.is-backward,
    &.is-forward
      width: auto
      display: flex

      &.is-backward::after,
      &.is-forward::before
        content: "…"
        color: $font-gray
        font-size: $font-small

      &.is-backward::after
        margin-left: 1em

      &.is-forward::before
        margin-right: 1em

  // spacer 用の DOM の解説は template 内のコメントを参照
  & &--spacer
    flex: 0 1 1em
    min-width: 0

  +breakpoint
    #{$root}--items
      width: 100%
      justify-content: space-between

    #{$root}--item
      flex-grow: 1
      display: flex

      &.is-prev,
      &.is-follow,
      &.is-current
        display: none

      &.is-forward
        justify-content: flex-end

      &.is-backward::after,
      &.is-forward::before
        display: none

    #{$root}--spacer
      display: none

.o-action__pagination
  $root: &

  & &--item
    display: block
    color: $font-black
    text-align: center
    text-decoration: none
    white-space: nowrap
    border-radius: 50%
    +transition

    +mouseover
      background-color: rgba($bg-black, 0.03)

  $icon-size-px: 4
  $font-size-px: 14
  $icon-size-em: math.div($icon-size-px, $font-size-px)
  $icon-margin-px: 14
  $icon-margin-em: math.div($icon-margin-px, $font-size-px)
  &.is-backward,
  &.is-forward
    #{$root}--item
      position: relative
      border-radius: 0

      &::before
        content: ""
        width: #{$icon-size-em}em
        height: #{$icon-size-em}em
        display: block
        position: absolute
        top: 50%

      +mouseover
        text-decoration: underline
        background-color: transparent

  &.is-backward
    #{$root}--item
      padding:
        left: #{$icon-size-em + $icon-margin-em}em
        right: 1.2em

      &::before
        left: 0
        transform: translateY(-50%) rotate(45deg)
        margin-left: 0.1em
        border:
          bottom: 2px solid $border-x-dark-gray
          left: 2px solid $border-x-dark-gray

  &.is-forward
    #{$root}--item
      padding:
        left: 1.2em
        right: #{$icon-size-em + $icon-margin-em}em

      &::before
        right: 0
        transform: translateY(-50%) rotate(-45deg)
        margin-right: 0.1em
        border:
          bottom: 2px solid $border-x-dark-gray
          right: 2px solid $border-x-dark-gray

  +breakpoint
    $icon-size-px: 4
    $font-size-px: 14
    $icon-size-em: math.div($icon-size-px, $font-size-px)
    $icon-margin-px: 14
    $icon-margin-em: math.div($icon-margin-px, $font-size-px)
    $button-margin-px: 28
    $button-margin-em: math.div($button-margin-px, $font-size-px)
    &.is-backward,
    &.is-forward
      #{$root}--item
        background-color: rgba($bg-white, 1)
        border: 1px solid $border-gray
        border-radius: 100vh

    &.is-backward
      #{$root}--item
        padding:
          left: #{$icon-size-em + $icon-margin-em + $button-margin-em}em
          right: #{$button-margin-em}em

        &::before
          left: #{$button-margin-em}em

    &.is-forward
      #{$root}--item
        padding:
          left: #{$button-margin-em}em
          right: #{$icon-size-em + $icon-margin-em + $button-margin-em}em

        &::before
          right: #{$button-margin-em}em
</style>
